import { ChevronDown, CirclePlus, ListFilter, Search, Star, TrendingUp, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { CostFilter, FilterDrawerProps } from '../types'
import { track } from '../lib/analytics'
import { CITY, splitAreas, type Area } from '../lib/areas'
import { FAVORITES_KIND } from '../lib/favorites'
import { POPULAR_KIND } from '../lib/popularity'
import { hostLabel } from '../lib/hostLabels'
import type { HostedByCount } from '../lib/useEvents'
import type { SearchSuggestion } from '../lib/search'
import { SearchHint } from './SearchHint'
import { readHintDismissed } from '../lib/searchHints'

/**
 * A filter worth offering at the moment that won't be later. Either a computed
 * occasion (Halloween, First Friday) or a curated windowed row from
 * lyh_filter_groups ("✨ New!"). The drawer doesn't care which: they behave the
 * same and share a section.
 */
export interface FeaturedOption {
  /** The ?group= value, so a chip shares like any other filter link. */
  slug: string
  label: string
  /** Upcoming events matching it. Zero is allowed ONLY for the Disaster
      Support chip (see isDisaster) - every other featured option is dropped
      by App before it gets here. */
  count: number
  /** The Disaster Support chip (lib/disasterResources): admin-toggled live via
      a windowed lyh_filter_groups row, pinned first regardless of sort, and
      rendered with a green cross rather than a plain pill - selecting it also
      opens the resources popup (see onSelectGroup below). */
  isDisaster?: boolean
}

interface ExtraProps {
  /** Empties the type boxes and nothing else, unlike the header's Clear All. */
  onClearTypes: () => void
  /** The query is FINISHED - Enter, blur, or a suggestion taken. It's what gets
      captured for analytics, so a pause in typing must never reach it. */
  onCommitQuery: (q: string) => void
  /** A suggestion was TAKEN (tapped, clicked, or Enter on a highlighted row), as
      opposed to text merely typed. The parent uses it to get the drawer out of
      the way on a phone so the matches are actually visible. */
  onSuggestionTaken?: () => void
  /** Text already in the calendar that matches what's been typed. */
  searchSuggestions: SearchSuggestion[]
  /** Example terms for the desktop search hint, already filtered to ones with
      something upcoming (lib/searchHints). Empty renders no hint at all. */
  searchHints: string[]
  /** Curated rows and occasions that are live now. Curated first. */
  featured: FeaturedOption[]
  /** Slug of the active group, seasonal or saved. One filters at a time. */
  groupSlug: string | null
  /** null leaves the filter and goes back to the whole calendar. */
  onSelectGroup: (slug: string | null) => void
  /** Opens the resources popup - fired alongside onSelectGroup when the
      Disaster Support chip is turned ON (see the Featured section below). */
  onOpenDisasterResources: () => void
  /** Select or clear a whole set of areas at once - the city and its
      neighborhoods, which have to move together rather than one at a time. */
  onToggleAreaGroup: (areas: Area[], selecting: boolean) => void
  /** Venues running enough events to be worth listing (see lib/useEvents). */
  hostedBy: HostedByCount[]
  /** Empty = every host, the same way an empty category selection means all. */
  selectedHostedBy: Set<string>
  onToggleHostedBy: (name: string) => void
  /** Events Andrew has flagged. Zero hides the filter entirely - see below. */
  pickCount: number
  picksOnly: boolean
  onTogglePicks: () => void
  /** Events starred on this device (lib/favorites). Zero hides the row, same
      rule as Picks - a box that would empty the calendar doesn't get offered. */
  favoritedCount: number
  /** Distinct names clearing the popularity threshold (lib/popularity) with at
      least one upcoming occurrence - the row count is "how many rows this box
      will show", matching Favorited/Picks. Zero hides the row, same rule. */
  popularCount: number
  /** Toggles the Popular sentinel AND its view-to-agenda side effect together
      (App.tsx) - not the generic onToggle, which only ever flips the Set. */
  onTogglePopular: () => void
}

// Wording matters more than it looks. "Free" has to mean "you can turn up and
// spend nothing", which is why a farmers market is Free and not a Deal -
// browsing one costs nothing (Andrew, 7/21).
//
// The LABELS say Free / Paid / Deal to match the key above the calendar (Lisa,
// 7/28: the drawer said Ticketed and Promo while the key said Paid and Deal, so
// the same three buckets had two sets of names). Display only - the values are
// still 'ticketed' and 'promo', so every ?cost= link that's been shared keeps
// working and nothing in the database moves.
const COST_OPTIONS: { value: CostFilter; label: string; hint: string }[] = [
  { value: 'all', label: 'All', hint: '' },
  { value: 'free', label: 'Free', hint: 'Turn up, spend nothing' },
  { value: 'ticketed', label: 'Paid', hint: 'Pay to get in' },
  { value: 'promo', label: 'Deal', hint: 'A deal to redeem' },
]

// 'cityAreas' isn't a Section - it's the neighborhood list nested under
// Lynchburg. It rides in the same map so there's one place collapse state is
// remembered rather than two.
type SectionId = 'comingUp' | 'hostedBy' | 'cost' | 'location' | 'type' | 'cityAreas'

const SECTIONS_KEY = 'lyh_filter_sections_v1'

// Everything that was already visible stays open, so the collapse (Lisa, 7/28)
// costs nobody the view they had. Hosted by is the exception: it's new and by
// far the longest list here, so it starts folded away.
const DEFAULT_OPEN: Record<SectionId, boolean> = {
  // Open: it only exists at all when a holiday is close AND has events, so the
  // whole point is that you see it without going looking.
  comingUp: true,
  hostedBy: false,
  cost: true,
  location: true,
  type: true,
  cityAreas: true,
}

function readOpenState(): Record<SectionId, boolean> {
  if (typeof window === 'undefined') return DEFAULT_OPEN
  try {
    const raw = window.localStorage.getItem(SECTIONS_KEY)
    if (!raw) return DEFAULT_OPEN
    // Spread the defaults under it: a section added after this was written is
    // missing from the stored object, and would otherwise read as collapsed.
    return { ...DEFAULT_OPEN, ...(JSON.parse(raw) as Partial<Record<SectionId, boolean>>) }
  } catch {
    return DEFAULT_OPEN // storage blocked or malformed -> the defaults still work
  }
}

interface SectionProps {
  id: SectionId
  label: string
  open: boolean
  onToggle: (id: SectionId) => void
  children: React.ReactNode
}

function Section({ id, label, open, onToggle, children }: SectionProps) {
  return (
    <div className="shrink-0 border-b border-hairline dark:border-slate-800">
      {/* Sticky inside the drawer's scroll container (Lisa, 7/28), so scrolling
          a long list - Hosted by is 47 rows - never leaves you looking at
          checkboxes with no idea which section they belong to. Needs an opaque
          background or the rows would slide underneath it. z-10 stays well below
          the z-30 the drawer's own stacking notes reserve. */}
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={`filter-section-${id}`}
        className="sticky top-0 z-10 flex w-full items-center justify-between bg-paper px-3 py-2.5 text-left hover:bg-white dark:bg-slate-900 dark:hover:bg-slate-800"
      >
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
            open ? '' : '-rotate-90'
          }`}
        />
      </button>
      {open && (
        <div id={`filter-section-${id}`} className="px-3 pb-2.5">
          {children}
        </div>
      )}
    </div>
  )
}

export function FilterDrawer(props: FilterDrawerProps & ExtraProps) {
  const { open, onClose, hosts, selected, onToggle, onSelectAll, onClear } = props
  const { cost, onChangeCost, costCounts } = props
  const { query, onChangeQuery, onCommitQuery, onSuggestionTaken, noMatches } = props
  const { areas, selectedAreas, onToggleArea, onToggleAreaGroup } = props
  const { hostedBy, selectedHostedBy, onToggleHostedBy } = props
  const { featured, groupSlug, onSelectGroup, onOpenDisasterResources } = props
  const { onClearTypes, searchSuggestions, searchHints } = props
  const { pickCount, picksOnly, onTogglePicks } = props
  const { favoritedCount } = props
  const { popularCount, onTogglePopular } = props

  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>(readOpenState)
  // Owned here, not in SearchHint: the input drops its bottom radius to meet the
  // hint, so both halves have to agree about whether it's showing.
  const [hintDismissed, setHintDismissed] = useState(readHintDismissed)
  // Only while the box is empty - once someone is typing they've found it, and
  // the suggestion dropdown owns that space.
  const showSearchHint =
    !hintDismissed && searchHints.length > 0 && query.trim().length === 0
  // Suggestions only while the box has focus, and dismissable with Escape
  // without also clearing what's been typed.
  const [searchFocused, setSearchFocused] = useState(false)
  const [suggestionsHidden, setSuggestionsHidden] = useState(false)
  const showSuggestions =
    searchFocused && !suggestionsHidden && searchSuggestions.length > 0
  // Which suggestion the arrow keys are on. -1 means none, which is the state
  // you are in while still typing: Enter then commits what you actually typed
  // rather than silently swapping in whatever happened to be first.
  const [activeSuggestion, setActiveSuggestion] = useState(-1)
  // Reset whenever the list itself changes, or the highlight would sit on a row
  // that has been replaced by a different suggestion under it.
  useEffect(() => {
    setActiveSuggestion(-1)
  }, [searchSuggestions])

  // Lynchburg plus its neighborhoods move as one box with children under it.
  const { city, towns } = splitAreas(areas)
  const cityGroup: Area[] = city ? [CITY, ...city.children.map((c) => c.area)] : []
  const cityAll = cityGroup.length > 0 && cityGroup.every((a) => selectedAreas.has(a))
  const citySome = cityGroup.some((a) => selectedAreas.has(a))

  const toggleSection = (id: SectionId) => {
    setOpenSections((prev) => {
      const next = { ...prev, [id]: !prev[id] }
      track('filter_section_toggled', { section: id, opening: next[id] })
      try {
        window.localStorage.setItem(SECTIONS_KEY, JSON.stringify(next))
      } catch {
        // Storage blocked (private mode). The panel still opens and closes; it
        // just won't be remembered next visit.
      }
      return next
    })
  }

  return (
    <>
      {/* Scrim is mobile-only: there the drawer covers the calendar, so the dim +
          tap-to-close is the way out. On desktop the drawer sits beside the
          calendar instead of over it, so a scrim would dim content that's still
          in use. */}
      {open && (
        <div
          className="absolute inset-0 z-30 bg-black/20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Below lg: an overlay that slides in over the calendar.
          From lg: an in-flow column that PUSHES the calendar across, so opening
          the filter never hides the first days of the week.

          z-40 (scrim z-30) because the page header is `sticky top-0 z-20` and
          lives inside <main>, which comes AFTER this drawer in the DOM. At equal
          z-index the later element wins, so the header painted straight through
          the open drawer and sliced the Cost control in half. Anything added to
          that header must stay below z-30. `lg:z-auto` still drops this out of
          the contest for the in-flow desktop layout. */}
      <aside
        // Full screen on a phone (Andrew, 7/28). At 375px the old 288px panel
        // left an 87px strip of calendar showing, too narrow to read and just
        // wide enough to look like a mistake, so below md the filters take the
        // whole screen and the button at the bottom is the way back.
        className={`absolute inset-y-0 left-0 z-40 flex w-full shrink-0 flex-col border-r border-hairline bg-paper shadow-xl transition-transform duration-200 ease-out md:w-72 lg:relative lg:z-auto lg:shadow-none lg:transition-[width] dark:border-slate-800 dark:bg-slate-900 ${
          open
            ? 'translate-x-0 lg:w-72'
            : '-translate-x-full pointer-events-none invisible lg:w-0 lg:overflow-hidden lg:border-r-0'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-hairline p-3 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <ListFilter className="h-4 w-4" />
              Filters
            </h2>
            {/* Clear All sits up here with the close button (Lisa, 7/28) rather
                than buried beside the type list, because it undoes every section
                at once - search, cost, location, hosted by, type, and any active
                group. */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onClear}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-white hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close filters"
                className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md p-1 sm:min-h-0 sm:min-w-0 text-slate-500 hover:bg-white hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Search, pinned above the scrolling sections: someone who already has a
            name in mind ("mahjong", "Needle Ninja") gets there in one move, where
            the sections below only help people browsing. Matches the event title,
            its details, the place and the category - not street addresses. */}
        <div className="shrink-0 border-b border-hairline px-3 py-2.5 dark:border-slate-800">
          <label htmlFor="filter-search" className="sr-only">
            Search events
          </label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            />
            {/* type="text", not "search": WebKit adds its own clear affordance to
                a search input, which would sit on top of the button below. */}
            <input
              id="filter-search"
              type="text"
              value={query}
              onChange={(e) => {
                setSuggestionsHidden(false)
                onChangeQuery(e.target.value)
              }}
              onFocus={() => setSearchFocused(true)}
              // A click on a suggestion fires its mousedown first, so the list
              // is still mounted when the pick lands.
              //
              // Blur is the commit signal for everyone who types a word and then
              // goes back to the calendar without pressing anything - on a phone
              // that's most of them. The suggestion buttons preventDefault on
              // mousedown, so taking one doesn't blur; that path commits itself.
              onBlur={() => {
                setSearchFocused(false)
                onCommitQuery(query)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSuggestionsHidden(true)
                  setActiveSuggestion(-1)
                  return
                }
                if (showSuggestions && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                  // preventDefault or the caret jumps to the end/start of the
                  // input while the highlight moves, which reads as two things
                  // responding to one key.
                  e.preventDefault()
                  const last = searchSuggestions.length - 1
                  setActiveSuggestion((i) => {
                    if (e.key === 'ArrowDown') return i >= last ? -1 : i + 1
                    return i <= -1 ? last : i - 1
                  })
                  return
                }
                if (e.key === 'Enter') {
                  const picked = searchSuggestions[activeSuggestion]
                  setSuggestionsHidden(true)
                  setActiveSuggestion(-1)
                  // A highlighted row wins; otherwise Enter commits the typed
                  // text, which is what it did before arrow keys existed.
                  if (showSuggestions && picked) {
                    track('search_suggestion_taken', { kind: picked.kind, via: 'keyboard' })
                    onChangeQuery(picked.value)
                    onCommitQuery(picked.value)
                    onSuggestionTaken?.()
                    return
                  }
                  onCommitQuery(query)
                }
              }}
              role="combobox"
              aria-expanded={showSuggestions}
              aria-controls="filter-search-suggestions"
              aria-activedescendant={
                showSuggestions && activeSuggestion >= 0
                  ? `filter-search-option-${activeSuggestion}`
                  : undefined
              }
              aria-autocomplete="list"
              placeholder="Search events, places..."
              className={`w-full rounded-md border border-hairline bg-white py-1.5 pl-7 pr-7 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brick-500 focus:outline-none focus:ring-1 focus:ring-brick-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 ${showSearchHint ? 'rounded-b-none' : ''}`}
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => onChangeQuery('')}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Typeahead (Lisa, 7/28). Every entry is text already on an event,
                so taking one always lands on results rather than the empty
                state. Places and categories only - a single event is not worth a
                row here (Andrew, 7/29), and the place rows carry the city and
                address so two venues with the same first word are told apart.
                Absolutely positioned over the sections below, which is why it
                needs the higher z-index: the sticky section headers are z-10 and
                would otherwise cut through it. */}
            {showSuggestions && (
              <ul
                id="filter-search-suggestions"
                role="listbox"
                className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border border-hairline bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
              >
                {searchSuggestions.map((s, i) => (
                  <li
                    key={`${s.kind}:${s.value}`}
                    id={`filter-search-option-${i}`}
                    role="option"
                    aria-selected={i === activeSuggestion}
                  >
                    <button
                      type="button"
                      // Hover moves the keyboard highlight too, so the mouse and
                      // the arrow keys can never disagree about which row Enter
                      // would take.
                      onMouseEnter={() => setActiveSuggestion(i)}
                      // mousedown, not click: the input's blur would unmount
                      // this list before a click ever arrived.
                      onMouseDown={(e) => {
                        e.preventDefault()
                        track('search_suggestion_taken', { kind: s.kind })
                        onChangeQuery(s.value)
                        onCommitQuery(s.value)
                        setSuggestionsHidden(true)
                        setActiveSuggestion(-1)
                        onSuggestionTaken?.()
                      }}
                      className={`flex w-full items-center gap-2 px-2 py-1.5 text-left ${
                        i === activeSuggestion
                          ? 'bg-paper dark:bg-slate-800'
                          : 'hover:bg-paper dark:hover:bg-slate-800'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-700 dark:text-slate-200">
                          {s.value}
                        </span>
                        {s.detail && (
                          <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                            {s.detail}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
                        {s.kind}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {noMatches && (
            // slate-400 on paper measured 2.43:1, under half the 4.5:1 AA floor,
            // on the one line a visitor reads at the moment the calendar has just
            // come up empty. slate-600 is 7.17:1 (Andrew, 9/20).
            <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300">
              Nothing matches that. Try fewer words, or a place name.
            </p>
          )}
          {/* Only while the box is empty: once someone is typing they've already
              found it, and the suggestion dropdown owns that space. */}
          {showSearchHint && (
            <SearchHint examples={searchHints} onDismiss={() => setHintDismissed(true)} />
          )}
        </div>

        {/* Past events used to have its own row here. Removed entirely (Lisa,
            8/19: "it should not be an option in the filter drawer at all") -
            past events now stay hidden unless an empty day or an empty agenda
            offers them from inside the calendar itself (CalendarView's
            onShowPast), which was already the roadmap's ask. */}

        {/* One scroll container for every section. Each section is shrink-0
            inside it, so any combination of expanded panels scrolls as a whole
            rather than squeezing whichever list happens to be last. */}
        <div className="flex-1 overflow-y-auto">
          {/* Featured. Two sources, one list: the computed occasions (a holiday
              approaching, the monthly First Friday) and curated rows carrying a
              window, of which "✨ New!" is the first - a grand opening stops
              being new, so its row names the date it should stop being offered.

              Called Featured rather than "Coming up" because a grand opening has
              usually already happened while it's still worth showing, where a
              holiday hasn't.

              The section only exists when something is BOTH in window and has
              events to show, so most of the time there's nothing here at all and
              the drawer opens on Hosted by. That's what keeps it from reading as
              the standing interests row Lisa asked to have taken out: it's
              usually absent, and when it's there it's a chip or two about
              something that's actually on. */}
          {featured.length > 0 && (
            <Section
              id="comingUp"
              label="Featured"
              open={openSections.comingUp}
              onToggle={toggleSection}
            >
              <div role="group" aria-label="Featured filters" className="flex flex-wrap gap-1">
                {featured.map(({ slug, label, count, isDisaster }) => {
                  const on = groupSlug === slug
                  return (
                    <button
                      key={slug}
                      type="button"
                      aria-pressed={on}
                      aria-label={label}
                      onClick={() => {
                        track('filter_featured_toggled', {
                          featured: slug,
                          selecting: !on,
                        })
                        onSelectGroup(on ? null : slug)
                        // Selecting it (not deselecting) also opens the
                        // resources popup - the chip's whole point.
                        if (isDisaster && !on) onOpenDisasterResources()
                      }}
                      className={`inline-flex min-h-9 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition md:min-h-0 ${
                        isDisaster
                          ? on
                            ? 'border-emerald-600 bg-emerald-600 text-white dark:border-emerald-400 dark:bg-emerald-500'
                            : 'border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-300 dark:hover:bg-emerald-500/10'
                          : on
                            ? 'border-brick-600 bg-brick-600 text-white dark:border-brick-300 dark:bg-brick-300 dark:text-slate-900'
                            : 'border-hairline text-slate-600 hover:bg-white dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`}
                    >
                      {isDisaster && (
                        <CirclePlus
                          aria-hidden="true"
                          className={`h-3.5 w-3.5 shrink-0 ${on ? '' : 'text-emerald-600 dark:text-emerald-400'}`}
                        />
                      )}
                      {label}
                      {count > 0 && <span className="ml-1 opacity-60">{count}</span>}
                    </button>
                  )
                })}
              </div>
            </Section>
          )}

          {/* Hosted by. The venue, which for most rows is the business putting the
              event on, plus any organiser layered on top of one (Hill City Mahj at
              Palmera House) - see hostedByNames in lib/useEvents for why this can't
              come from the `host` column. First section, the slot the interest pills
              held until Lisa's second pass dropped them (7/28). Empty selection
              means everyone. */}
          {hostedBy.length > 0 && (
            <Section
              id="hostedBy"
              label="Hosted by"
              open={openSections.hostedBy}
              onToggle={toggleSection}
            >
              {hostedBy.map(({ name, count }) => (
                <label
                  key={name}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-white dark:hover:bg-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={selectedHostedBy.has(name)}
                    onChange={() => {
                      track('filter_hosted_by_toggled', {
                        host: name,
                        selecting: !selectedHostedBy.has(name),
                      })
                      onToggleHostedBy(name)
                    }}
                    className="h-3.5 w-3.5 shrink-0 accent-slate-700 dark:accent-slate-300"
                  />
                  <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                    {name}
                  </span>
                  <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                    {count}
                  </span>
                </label>
              ))}
            </Section>
          )}

          {/* Cost. Above the type list on purpose: what it costs is the cut
              people arrive with, where a type is something they browse into. */}
          <Section id="cost" label="Cost" open={openSections.cost} onToggle={toggleSection}>
            <div role="group" aria-label="Filter by cost" className="grid grid-cols-2 gap-1">
              {COST_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={cost === opt.value}
                  // No aria-label (2026-09-23): "Free" as the whole name left out
                  // the count printed right beside it, which PageSpeed flags as
                  // label-content-name-mismatch and which breaks voice control
                  // ("click Free 12"). The content is the name now, "Free 12".
                  title={opt.hint}
                  onClick={() => {
                    track('cost_filter_toggled', { to: opt.value })
                    onChangeCost(opt.value)
                  }}
                  className={`min-h-9 rounded-md border px-2 py-1.5 text-xs font-medium transition md:min-h-0 ${
                    cost === opt.value
                      ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                      : 'border-hairline text-slate-600 hover:bg-white dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  {opt.label}
                  {/* The count was opacity-60, which composites slate-600 on
                      paper down to 2.82:1 at 12px. It is still set apart by the
                      normal weight against the medium label, just no longer
                      faded past legibility: 7.18:1. Unselected state only; on
                      the selected slate-900 fill it is white, 17.83:1. The
                      space keeps the computed name "Free 12", not "Free12". */}
                  {opt.value !== 'all' && (
                    <>
                      {' '}
                      <span className="ml-0.5 font-normal">{costCounts[opt.value]}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </Section>

          {/* Location. Derived from the venue string rather than a column (see
              lib/areas), which is why it lists only the areas that actually have
              events - and why it can never grow past the handful in AREAS. Empty
              selection means every area.

              The city's neighborhoods are indented under Lynchburg (Andrew,
              7/28). Ticking Lynchburg ticks them with it: a venue is filed under
              Downtown OR Lynchburg and never both, so the city on its own would
              quietly drop the Downtown events. Untick a child and the parent goes
              indeterminate, which is the honest reading of "most of the city". */}
          {areas.length > 1 && (
            <Section
              id="location"
              label="Location"
              open={openSections.location}
              onToggle={toggleSection}
            >
              {city && (
                <>
                  {/* The chevron is its own button OUTSIDE the label, or tapping
                      it would tick the city as well as fold the list (Lisa,
                      7/28 - with the neighborhoods filled out, Lynchburg alone
                      is most of the section). */}
                  <div className="flex items-center">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-white dark:hover:bg-slate-800">
                      <input
                        type="checkbox"
                        checked={cityAll}
                        // indeterminate is a DOM property, not an attribute, so
                        // it can only be set through the node itself.
                        ref={(el) => {
                          if (el) el.indeterminate = citySome && !cityAll
                        }}
                        onChange={() => {
                          track('filter_area_toggled', { area: CITY, selecting: !cityAll })
                          onToggleAreaGroup(cityGroup, !cityAll)
                        }}
                        className="h-3.5 w-3.5 shrink-0 accent-slate-700 dark:accent-slate-300"
                      />
                      <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                        {city.area}
                      </span>
                      <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                        {city.count}
                      </span>
                    </label>
                    {city.children.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleSection('cityAreas')}
                        aria-expanded={openSections.cityAreas}
                        aria-controls="filter-city-areas"
                        aria-label={`${openSections.cityAreas ? 'Hide' : 'Show'} ${city.area} neighborhoods`}
                        className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                      >
                        <ChevronDown
                          aria-hidden="true"
                          className={`h-3.5 w-3.5 transition-transform ${
                            openSections.cityAreas ? '' : '-rotate-90'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  <div id="filter-city-areas" hidden={!openSections.cityAreas}>
                  {city.children.map(({ area, count }) => (
                    <label
                      key={area}
                      className="flex cursor-pointer items-center gap-2 rounded-md py-1 pl-6 pr-1 hover:bg-white dark:hover:bg-slate-800"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAreas.has(area)}
                        onChange={() => {
                          track('filter_area_toggled', {
                            area,
                            selecting: !selectedAreas.has(area),
                          })
                          onToggleArea(area)
                        }}
                        className="h-3.5 w-3.5 shrink-0 accent-slate-700 dark:accent-slate-300"
                      />
                      <span className="truncate text-sm text-slate-600 dark:text-slate-300">
                        {area}
                      </span>
                      <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                        {count}
                      </span>
                    </label>
                  ))}
                  </div>
                </>
              )}
              {towns.map(({ area, count }) => (
                <label
                  key={area}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-white dark:hover:bg-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={selectedAreas.has(area)}
                    onChange={() => {
                      track('filter_area_toggled', {
                        area,
                        selecting: !selectedAreas.has(area),
                      })
                      onToggleArea(area)
                    }}
                    className="h-3.5 w-3.5 shrink-0 accent-slate-700 dark:accent-slate-300"
                  />
                  <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                    {area}
                  </span>
                  <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                    {count}
                  </span>
                </label>
              ))}
            </Section>
          )}

          {/* Event type. The `host` column: the 15 kinds an event can be filed
              under.

              Select all / Clear all are a pair and both act on THIS list only
              (Lisa, 7/28). The header's Clear All is the drawer-wide one; these
              sit inside the section they apply to, which is the only thing
              distinguishing them, so don't move them out of it. */}
          <Section id="type" label="Event type" open={openSections.type} onToggle={toggleSection}>
            {/* min-h-6 because a bare text-xs button is a 16px-tall target, and
                these two sit 12px apart; PageSpeed's desktop target-size audit
                flagged both (2026-09-23, WCAG 2.5.8 asks for 24px). The row is
                8px taller, nothing else moves. slate-600 rather than 500 because
                slate-500 on paper is 4.51:1, AA to two decimal places for 12px
                text; 600 is 7.18:1, with hover going darker still. */}
            <div className="mb-1 flex items-center gap-3">
              <button
                type="button"
                onClick={onSelectAll}
                className="inline-flex min-h-6 items-center text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={onClearTypes}
                className="inline-flex min-h-6 items-center text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Clear all
              </button>
            </div>

            {/* Favorited Events, at the very top of the list and bold (Lisa,
                8/19). Unlike Picks below, this rides in the SAME `selected` set
                as the real categories rather than its own boolean - the spec
                asks for it to combine with other types the way two kinds
                combine (a union: "Favorited OR Live Music"), not AND them the
                way Picks narrows within a category. lib/favorites' sentinel is
                what makes that possible without polluting eventKinds.
                Hidden at zero, same rule as Picks: a box that would empty the
                calendar isn't offered. */}
            {favoritedCount > 0 && (
              <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-md border-b border-hairline px-1 pb-1.5 hover:bg-white dark:border-slate-800 dark:hover:bg-slate-800">
                <input
                  type="checkbox"
                  checked={selected.has(FAVORITES_KIND)}
                  onChange={() => {
                    track('filter_type_toggled', {
                      type: FAVORITES_KIND,
                      selecting: !selected.has(FAVORITES_KIND),
                    })
                    onToggle(FAVORITES_KIND)
                  }}
                  className="h-3.5 w-3.5 shrink-0 accent-amber-500"
                />
                <Star
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400"
                />
                <span className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                  Favorited Events
                </span>
                <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                  {favoritedCount}
                </span>
              </label>
            )}

            {/* Andrew's Picks sits above the categories and is ruled off from
                them, because it is not a sixteenth category - it cuts across all
                of them. Ticking it alongside a category narrows to the picks of
                that kind, the way every other pair of filters in this drawer
                behaves.

                Hidden entirely at zero. A category with no events never appears
                here either (the list is built from the events themselves), and
                an always-visible box that empties the calendar the moment it is
                touched is the same bad first impression the Featured chips are
                gated against. */}
            {pickCount > 0 && (
              <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-md border-b border-hairline px-1 pb-1.5 hover:bg-white dark:border-slate-800 dark:hover:bg-slate-800">
                <input
                  type="checkbox"
                  checked={picksOnly}
                  onChange={() => {
                    track('filter_picks_toggled', { selecting: !picksOnly })
                    onTogglePicks()
                  }}
                  className="h-3.5 w-3.5 shrink-0 accent-brick-600 dark:accent-brick-300"
                />
                <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                  Andrew's Picks!
                </span>
                <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                  {pickCount}
                </span>
              </label>
            )}

            {/* Popular sits above the alphabetical categories, last of the
                pinned rows above it (Favorited, Picks) - it is NOT a host
                category (App.tsx has no `host` value for it), it's a
                sentinel riding in the same `selected` Set, the same trick
                FAVORITES_KIND already plays (lib/popularity, App.tsx's
                `matched`/`shownEvents`). Selecting it also flips the
                calendar into agenda view - see onTogglePopular in App.tsx for
                why that lives there and not here. Hidden at zero, same rule
                as Favorited and Picks above. */}
            {popularCount > 0 && (
              <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-md border-b border-hairline px-1 pb-1.5 hover:bg-white dark:border-slate-800 dark:hover:bg-slate-800">
                <input
                  type="checkbox"
                  checked={selected.has(POPULAR_KIND)}
                  onChange={() => {
                    track('filter_popular_toggled', {
                      selecting: !selected.has(POPULAR_KIND),
                    })
                    onTogglePopular()
                  }}
                  className="h-3.5 w-3.5 shrink-0 accent-orange-500"
                />
                <TrendingUp
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 text-orange-500"
                />
                <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                  Popular
                </span>
                <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                  {popularCount}
                </span>
              </label>
            )}

            {hosts.map(({ host, count }) => (
              <label
                key={host}
                className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-white dark:hover:bg-slate-800"
              >
                <input
                  type="checkbox"
                  checked={selected.has(host)}
                  onChange={() => {
                    // Which types people filter TO is the most useful signal here:
                    // it says what they came looking for.
                    track('filter_type_toggled', {
                      type: host,
                      selecting: !selected.has(host),
                    })
                    onToggle(host)
                  }}
                  className="h-3.5 w-3.5 shrink-0 accent-slate-700 dark:accent-slate-300"
                />
                <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                  {hostLabel(host)}
                </span>
                <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">{count}</span>
              </label>
            ))}
          </Section>
        </div>

        {/* Phone only. With the filters covering the whole screen there's no
            calendar left to tap to get back, so the way out is a button rather
            than a guess. It only closes: the picks are already applied, which is
            why it says what you'll see and not "Apply". pb-safe keeps it clear
            of the iOS home indicator. */}
        <div className="shrink-0 border-t border-hairline bg-paper p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden dark:border-slate-800 dark:bg-slate-900">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-brick-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-brick-700"
          >
            Show events
          </button>
        </div>
      </aside>
    </>
  )
}
